import { TaskRecordPicker } from './TaskRecordPicker';
import { TaskScheduleFields } from './TaskScheduleFields';
import React, { useState, useEffect } from 'react';
import {
  Alert,
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Option,
  Button,
  Stack,
  Grid,
  Slider,
  CircularProgress,
} from '@mui/joy';
// Date picker imports removed for simplicity
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import type { Task, TaskUpdateData, TaskType, TaskPriority, TaskStatus } from '../../types/tasks';
import { useOrganizationUsers } from '../../hooks/useOrganizationUsers';

interface TaskEditModalProps {
  open: boolean;
  task: Task;
  onClose: () => void;
  onSuccess: (updatedTask?: Task) => void;
}

const taskTypeOptions: { value: TaskType; label: string }[] = [
  { value: 'phone_call', label: 'Phone Call' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'site_visit', label: 'Site Visit' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'installation', label: 'Installation' },
  { value: 'repair', label: 'Repair' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'documentation', label: 'Documentation' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'material_order', label: 'Material Order' },
  { value: 'scheduling', label: 'Scheduling' },
  { value: 'cleanup', label: 'Cleanup' },
  { value: 'other', label: 'Other' },
];

export const TaskEditModal: React.FC<TaskEditModalProps> = ({
  open,
  task,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<Partial<TaskUpdateData>>({});

  // Fetch organization users
  const { users, loading: usersLoading } = useOrganizationUsers();

  useEffect(() => {
    if (task) {
      setFormData({
        name: task.name,
        description: task.description,
        instructions: task.instructions,
        task_type: task.task_type,
        priority: task.priority,
        status: task.status,
        is_all_day: task.is_all_day,
        due_date: task.due_date,
        estimated_hours: task.estimated_hours,
        actual_hours: task.actual_hours,
        completion_percentage: task.completion_percentage,
        related_job: task.related_job,
        related_opportunity: task.related_opportunity,
        assigned_to: task.assigned_to,
      });
    }
  }, [task]);

  // Update task mutation
  const updateMutation = useMutation({
    mutationFn: async (data: Partial<TaskUpdateData>): Promise<Task> => {
      const response = await apiClient.patch(`/api/tasks/${task.id}/`, data);
      return response.data as Task;
    },
    onSuccess: (updatedTask: Task) => {
      onSuccess(updatedTask);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: '90vw', maxWidth: 800, overflow: 'auto' }}>
        <ModalClose />
        <Typography level="h4">Edit Task</Typography>

        <form onSubmit={handleSubmit}>
          {updateMutation.isError && <Alert color="danger">{String((updateMutation.error as any)?.message || 'Unable to save task. Check the fields and prerequisites.')}</Alert>}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Left Column */}
            <Grid xs={12} md={6}>
              <Stack spacing={2}>
                <FormControl required>
                  <FormLabel>Task Name</FormLabel>
                  <Input
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter task name"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter task description"
                    minRows={3}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Instructions</FormLabel>
                  <Textarea
                    value={formData.instructions || ''}
                    onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                    placeholder="Enter detailed instructions for completing this task"
                    minRows={3}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Completion Percentage</FormLabel>
                  <Stack spacing={2} direction="row" alignItems="center">
                    <Slider
                      value={formData.completion_percentage || 0}
                      onChange={(_, value) =>
                        setFormData({ ...formData, completion_percentage: value as number })
                      }
                      valueLabelDisplay="auto"
                      step={10}
                      marks
                      min={0}
                      max={100}
                    />
                    <Typography>{formData.completion_percentage || 0}%</Typography>
                  </Stack>
                </FormControl>
              </Stack>
            </Grid>

            {/* Right Column */}
            <Grid xs={12} md={6}>
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  <Grid xs={6}>
                    <FormControl>
                      <FormLabel>Task Type</FormLabel>
                      <Select
                        value={formData.task_type}
                        onChange={(_, value) => setFormData({ ...formData, task_type: value as TaskType })}
                      >
                        {taskTypeOptions.map((option) => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid xs={6}>
                    <FormControl>
                      <FormLabel>Priority</FormLabel>
                      <Select
                        value={formData.priority}
                        onChange={(_, value) => setFormData({ ...formData, priority: value as TaskPriority })}
                      >
                        <Option value="low">Low</Option>
                        <Option value="medium">Medium</Option>
                        <Option value="high">High</Option>
                        <Option value="critical">Critical</Option>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select
                    value={formData.status}
                    onChange={(_, value) => setFormData({ ...formData, status: value as TaskStatus })}
                  >
                    <Option value="not_started">Not Started</Option>
                    <Option value="in_progress">In Progress</Option>
                    <Option value="on_hold">On Hold</Option>
                    <Option value="completed">Completed</Option>
                    <Option value="cancelled">Cancelled</Option>
                  </Select>
                </FormControl>

                <TaskRecordPicker label="Related Job" endpoint="/api/jobs/" value={formData.related_job} onChange={record => setFormData({ ...formData, related_job: record?.id ?? null, related_opportunity: record ? null : formData.related_opportunity })} />

                <TaskRecordPicker label="Related Opportunity" endpoint="/api/crm/opportunities/" value={formData.related_opportunity} onChange={record => setFormData({ ...formData, related_opportunity: record?.id ?? null, related_job: record ? null : formData.related_job })} />

                <FormControl>
                  <FormLabel>Assigned To</FormLabel>
                  <Select
                    value={formData.assigned_to?.toString() || ''}
                    onChange={(_, value) => setFormData({ ...formData, assigned_to: value ? parseInt(value) : null })}
                    placeholder="Select a user"
                    disabled={usersLoading}
                    endDecorator={usersLoading ? <CircularProgress size="sm" /> : null}
                  >
                    <Option value="">None</Option>
                    {(users || []).map((user) => (
                      <Option key={user.id} value={user.id.toString()}>
                        {user.first_name} {user.last_name}
                      </Option>
                    ))}
                  </Select>
                </FormControl>

                <Grid container spacing={2}>
                  <Grid xs={12} sm={6}>
                    <TaskScheduleFields due={formData.due_date} allDay={formData.is_all_day} assignedTo={formData.assigned_to} onChange={(due, allDay) => setFormData({ ...formData, due_date: due ?? null, is_all_day: allDay })} />
                  </Grid>
                  <Grid xs={6} sm={3}>
                    <FormControl>
                      <FormLabel sx={{ whiteSpace: 'nowrap' }}>Est. Hours</FormLabel>
                      <Input
                        type="number"
                        value={formData.estimated_hours || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            estimated_hours: e.target.value ? parseFloat(e.target.value) : undefined
                          })
                        }
                        placeholder="0"
                        slotProps={{ input: { min: 0, step: 0.5 } }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid xs={6} sm={3}>
                    <FormControl>
                      <FormLabel sx={{ whiteSpace: 'nowrap' }}>Actual Hours</FormLabel>
                      <Input
                        type="number"
                        value={formData.actual_hours || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            actual_hours: e.target.value ? parseFloat(e.target.value) : undefined
                          })
                        }
                        placeholder="0"
                        slotProps={{ input: { min: 0, step: 0.5 } }}
                      />
                    </FormControl>
                  </Grid>
                </Grid>

                
              </Stack>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button variant="outlined" color="neutral" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={updateMutation.isPending}
              disabled={!formData.name}
            >
              Save Changes
            </Button>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
};